import React, { Fragment } from 'react';

import classNames from 'classnames';

import { formatActivityDate, formatActivityDateTime, STATUS_ACTIVE, STATUS_INVITE_EXPIRED } from './userActivityFormat';
import UserLoginHistory from './UserLoginHistory';

import ChevronDown from 'Assets/icons/chevron-down.svg';
import ChevronUp from 'Assets/icons/chevron-up.svg';

const COLUMN_COUNT = 9;

// The shared table draws a divider between every cell and fills rows grey;
// this table keeps only the row dividers on white rows, as designed. Both
// rules are more specific than a plain utility, hence the `!`.
const CELL = 'tw-text-start !tw-border-r-0';
const ROW = '!tw-bg-white';

// Badge's colours without its leading dot: the design's pills are text only.
const STATUS_PILLS = {
    [STATUS_ACTIVE]: { className: 'tw-bg-green-25 tw-text-green-600', label: () => $t('Active') },
    [STATUS_INVITE_EXPIRED]: { className: 'tw-bg-charcoal-15 tw-text-charcoal-75', label: () => $t('Invite expired') },
    invited: { className: 'tw-bg-amber-100 tw-text-amber-700', label: () => $t('Invited') },
};

function Muted({ children }) {
    return <span className="tw-text-charcoal-50">{children}</span>;
}

function DateCell({ value, empty, withTime = false }) {
    if (!value) {
        return <Muted>{empty}</Muted>;
    }

    return withTime ? formatActivityDateTime(value) : formatActivityDate(value);
}

function StatusPill({ status }) {
    const pill = STATUS_PILLS[status] || STATUS_PILLS.invited;

    return (
        <span className={classNames('tw-inline-flex tw-items-center tw-rounded-md tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium', pill.className)}>
            {pill.label()}
        </span>
    );
}

function UserActivityRow({ row, expanded, onToggle, historyUrl }) {
    return (
        <Fragment>
            <tr className={ROW}>
                <td className={classNames(CELL, 'tw-w-10')}>
                    {row.logins_count > 0 && (
                        <button
                            type="button"
                            className="tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded tw-bg-charcoal-10 hover:tw-bg-charcoal-15"
                            aria-expanded={expanded}
                            aria-label={expanded ? $t('Hide login history') : $t('Show login history')}
                            onClick={() => onToggle(row.id)}
                        >
                            {expanded ? <ChevronUp className="tw-w-2.5 tw-h-2.5" /> : <ChevronDown className="tw-w-2.5 tw-h-2.5" />}
                        </button>
                    )}
                </td>
                <td className={classNames(CELL, 'tw-font-medium')} title={row.email}>
                    {row.name}
                </td>
                <td className={CELL}>{row.role ? $t(row.role) : <Muted>—</Muted>}</td>
                <td className={CELL}>
                    <DateCell value={row.invite_sent_at} empty={$t('Not recorded')} />
                </td>
                <td className={CELL}>
                    <DateCell value={row.invite_accepted_at} empty={$t('Not yet')} />
                </td>
                <td className={CELL}>
                    {/* Unknown for a member who joined before login history begins. */}
                    <DateCell value={row.first_login_at} empty={row.first_login_recorded ? $t('Never') : $t('Not recorded')} />
                </td>
                <td className={CELL}>
                    <DateCell value={row.last_login_at} empty={$t('Never')} withTime />
                </td>
                <td className={CELL}>{row.logins_count}</td>
                <td className={CELL}>
                    <StatusPill status={row.status} />
                </td>
            </tr>
            {expanded && (
                <tr className={ROW}>
                    <td className={CELL} />
                    <td colSpan={COLUMN_COUNT - 1} className={classNames(CELL, '!tw-p-0 !tw-bg-charcoal-10')}>
                        <UserLoginHistory historyUrl={historyUrl.replace('__user__', row.id)} total={row.logins_count} />
                    </td>
                </tr>
            )}
        </Fragment>
    );
}

export default function UserActivityTable({ rows, expandedIds, onToggle, historyUrl }) {
    return (
        <div className="inbud-table_wrapper">
            <table className="inbud-table">
                <thead>
                    <tr>
                        <th aria-label={$t('Login history')} />
                        <th className="!tw-text-start">{$t('Name')}</th>
                        <th className="!tw-text-start">{$t('Role')}</th>
                        <th className="!tw-text-start">{$t('Invite sent')}</th>
                        <th className="!tw-text-start">{$t('Invite accepted')}</th>
                        <th className="!tw-text-start">{$t('First login')}</th>
                        <th className="!tw-text-start">{$t('Last login')}</th>
                        <th className="!tw-text-start">{$t('Logins')}</th>
                        <th className="!tw-text-start">{$t('Status')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr className={ROW}>
                            <td colSpan={COLUMN_COUNT} className={classNames(CELL, '!tw-text-center tw-text-charcoal-75')}>
                                {$t('No users match these filters.')}
                            </td>
                        </tr>
                    )}
                    {rows.map((row) => (
                        <UserActivityRow key={row.id} row={row} expanded={expandedIds.includes(row.id)} onToggle={onToggle} historyUrl={historyUrl} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}
